import React, { useState, useEffect } from 'react';

export interface Solicitud {
    id: string;
    folio: string;
    ejecutivo: string;
    categoria: string;
    origen: string;

    clienteId: string;
    clienteNombre: string;
    contratoId: string;
    contratoNombre: string;
    flotilla: string;

    bienId: string;
    numeroSerie: string;
    marca: string;
    modelo: string;
    anio: string;
    version: string;

    fechaCita: string | null;
    taller: string | null;

    comentarios: string;

    pasoSeguimiento: number | string;
    estatusSeguimiento: string;
    diasSla: number;
    horasTaller: number;
    atendida: boolean;
    colorBorde: string;
}

export interface Executive {
    clave_usuario: number;
    usuario: string;
    nombre_completo: string;
    clave_tipo_usuario: string;
    rol: string;
}

export enum RequestFlowId {
    UNASSIGNED = 0,
    ASSIGN_REQUEST = 1,
    BOOK_APPOINTMENT = 2,
    ENTER_ASSET = 3,
    DIAGNOSIS = 4,
    SHOP_TRACKING = 5,
    DELIVERY_TRACKING = 6,
    CLOSE_ORDER = 7,
}

export const RequestFlowLabels: Record<RequestFlowId, string> = {
    [RequestFlowId.UNASSIGNED]: '---',
    [RequestFlowId.ASSIGN_REQUEST]: 'Asignar solicitud',
    [RequestFlowId.BOOK_APPOINTMENT]: 'Agendar cita',
    [RequestFlowId.ENTER_ASSET]: 'Ingresar bien',
    [RequestFlowId.DIAGNOSIS]: 'Diagnóstico',
    [RequestFlowId.SHOP_TRACKING]: 'Seguimiento de taller',
    [RequestFlowId.DELIVERY_TRACKING]: 'Seguimiento a entrega',
    [RequestFlowId.CLOSE_ORDER]: 'Cerrar orden',
};

type FilterType = 'TODAS' | 'OPERACION' | 'SINIESTROS';
type SortKey = 'folio' | 'diasSla' | 'diasTaller' | 'clienteNombre' | 'numeroSerie';
type SortDirection = 'asc' | 'desc';

const DashboardSolicitudes: React.FC = () => {
    const [solicitudes, setSolicitudes] = useState<Solicitud[]>([]);
    const [executiveResults, setExecutiveResults] = useState<Executive[]>([]);
    const [conteoSolicitudes, setConteoSolicitudes] = useState<number>(0);
    const [conteoTotalSolicitudes, setConteoTotalSolicitudes] = useState<number>(0);
    const [cargando, setCargando] = useState<boolean>(true);

    const [filtroActivo, setFiltroActivo] = useState<FilterType>('OPERACION');
    const [busqueda, setBusqueda] = useState<string>("");
    const [executive, setExecutive] = useState<string>("");
    const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: SortDirection }>({ key: 'diasSla', direction: 'desc' });

    const [paginaActual, setPaginaActual] = useState<number>(1);
    const registrosPorPagina = 6;

    const totalPaginas = Math.ceil(conteoSolicitudes / registrosPorPagina) || 1;

    useEffect(() => {
        const fetchSolicitudes = async () => {
            setCargando(true);
            try {
                const queryParams = new URLSearchParams({
                    filtro: filtroActivo,
                    search: busqueda,
                    page: paginaActual.toString(),
                    registros: registrosPorPagina.toString(),
                    ejecutivo: executive,
                    sort_key: sortConfig.key,
                    sort_dir: sortConfig.direction
                });

                const response = await fetch(`/sittApi/dashboard_solicitudes.php?${queryParams.toString()}`);
                const result = await response.json();

                if (result.status === 'success') {
                    setSolicitudes(result.data);
                    setConteoTotalSolicitudes(result.total || 0);
                    const badgeElement = document.getElementById('badge-total-solicitudes');
                    if (badgeElement) {
                        badgeElement.innerText = `(${result.total || 0})`;
                    }
                    setConteoSolicitudes(result.total || 0);
                }
            } catch (error) {
                console.error("Error al obtener solicitudes", error);
            } finally {
                setCargando(false);
            }
        };

        const delayDebounceFn = setTimeout(() => {
            fetchSolicitudes();
        }, 300);

        return () => clearTimeout(delayDebounceFn);

    }, [filtroActivo, busqueda, paginaActual, sortConfig, executive]);

    const fetchExecutives = async () => {
        try {
            const response = await fetch('/sittApi/obtener_usuarios_por_rol.php?key_label=EJECUTIVO_FLOTILLA');
            const data = await response.json();
            setExecutiveResults(data.data || []);
        } catch (error) {
            console.error("Error searching executives", error);
        }
    };

    useEffect(() => {
        fetchExecutives();
    }, []);

    const handleSort = (key: SortKey) => {
        setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
    };

    const handleCambiarPagina = (nuevaPagina: number) => {
        if (nuevaPagina >= 1 && nuevaPagina <= totalPaginas) {
            setPaginaActual(nuevaPagina);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = e.target;
        setExecutive(value);
    };

    useEffect(() => {
        setPaginaActual(1);
    }, [filtroActivo, busqueda, sortConfig]);

    const renderSlaBadge = (dias: number) => {
        if (dias === 0) return <span className="fw-bold text-dark">Hoy</span>;
        if (dias < 0) return <span className="badge bg-danger" style={{ fontSize: "1.10em" }}> Hace {Math.abs(dias)} día(s)</span >;
        return <span className="text-muted">En {dias} día(s)</span>;
    };

    const renderTallerBadge = (horas: number) => {
        if (horas === 0) return <span className="fw-bold text-dark"></span>;
        if (horas < 0) return <span className="badge bg-danger" style={{ fontSize: "1.10em" }}> Durante {Math.abs(horas)} hora(s)</span >;
        return <span className="text-muted">Durante {horas} hora(s)</span>;
    };

    const ProgressBar = ({ pasoActual }: { pasoActual: number | string }) => {
        const pasos = [
            { id: RequestFlowId.ASSIGN_REQUEST, icon: 'fa-user-check', title: RequestFlowLabels[RequestFlowId.ASSIGN_REQUEST] },
            { id: RequestFlowId.BOOK_APPOINTMENT, icon: 'fa-calendar-check', title: RequestFlowLabels[RequestFlowId.BOOK_APPOINTMENT] },
            { id: RequestFlowId.ENTER_ASSET, icon: 'fa-building', title: RequestFlowLabels[RequestFlowId.ENTER_ASSET] },
            { id: RequestFlowId.DIAGNOSIS, icon: 'fa-stethoscope', title: RequestFlowLabels[RequestFlowId.DIAGNOSIS] },
            { id: RequestFlowId.SHOP_TRACKING, icon: 'fa-tools', title: RequestFlowLabels[RequestFlowId.SHOP_TRACKING] },
            { id: RequestFlowId.DELIVERY_TRACKING, icon: 'fa-car-side', title: RequestFlowLabels[RequestFlowId.DELIVERY_TRACKING] },
            { id: RequestFlowId.CLOSE_ORDER, icon: 'fa-check-circle', title: RequestFlowLabels[RequestFlowId.CLOSE_ORDER] },
        ];

        const currentStep = Number(pasoActual);

        return (
            <div className="d-flex align-items-center justify-content-center mb-2 w-100" style={{ gap: '2px' }}>
                {pasos.map((paso, index) => {
                    const isCompleted = paso.id < currentStep;
                    const isActive = paso.id === currentStep;

                    let bgColor = '#e9ecef';
                    let color = '#6c757d';

                    if (isCompleted) {
                        bgColor = '#198754';
                        color = 'white';
                    } else if (isActive) {
                        bgColor = '#0d6efd';
                        color = 'white';
                    }

                    return (
                        <React.Fragment key={paso.id}>
                            <div
                                title={paso.title}
                                className="rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                                style={{
                                    width: '24px',
                                    height: '24px',
                                    backgroundColor: bgColor,
                                    color: color,
                                    fontSize: '0.7rem',
                                    fontWeight: isActive ? 'bold' : 'normal',
                                    flexShrink: 0
                                }}
                            >
                                {isCompleted ? (
                                    <i className="fas fa-check"></i>
                                ) : (
                                    <i className={`far ${paso.icon}`}></i>
                                )}
                            </div>
                            {index < pasos.length - 1 && (
                                <div style={{
                                    height: '2px',
                                    minWidth: '5px',
                                    flexGrow: 1,
                                    backgroundColor: isCompleted ? '#198754' : '#e9ecef'
                                }}></div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    const renderLabel = (solicitud: { pasoSeguimiento: any; }) => {
        switch (Number(solicitud.pasoSeguimiento)) {
            case RequestFlowId.UNASSIGNED:
                return RequestFlowLabels[RequestFlowId.UNASSIGNED];
            case RequestFlowId.ASSIGN_REQUEST:
                return RequestFlowLabels[RequestFlowId.ASSIGN_REQUEST];
            case RequestFlowId.BOOK_APPOINTMENT:
                return RequestFlowLabels[RequestFlowId.BOOK_APPOINTMENT];
            case RequestFlowId.ENTER_ASSET:
                return RequestFlowLabels[RequestFlowId.ENTER_ASSET];
            case RequestFlowId.DIAGNOSIS:
                return RequestFlowLabels[RequestFlowId.DIAGNOSIS];
            case RequestFlowId.SHOP_TRACKING:
                return RequestFlowLabels[RequestFlowId.SHOP_TRACKING];
            case RequestFlowId.DELIVERY_TRACKING:
                return RequestFlowLabels[RequestFlowId.DELIVERY_TRACKING];
            case RequestFlowId.CLOSE_ORDER:
                return RequestFlowLabels[RequestFlowId.CLOSE_ORDER];
            default:
                return "Seguimiento";
        }
    };

    return (
        <div className="card shadow-sm border-0">
            <div className="card-body border-bottom d-flex justify-content-between align-items-center p-3">
                <div className="btn-group shadow-sm">
                    <button style={{ borderColor: "#b1b1b1", borderRadius: "2px" }} onClick={() => setFiltroActivo('OPERACION')} className={`btn btn-sm ${filtroActivo === 'OPERACION' ? 'btn-primary' : ''}`}>Solicitudes de servicio {filtroActivo == "OPERACION" ? `(${conteoSolicitudes.toString()})` : ""}</button>
                    <button style={{ borderColor: "#b1b1b1", borderRadius: "2px" }} onClick={() => setFiltroActivo('SINIESTROS')} className={`btn btn-sm ${filtroActivo === 'SINIESTROS' ? 'btn-primary' : ''}`}>Siniestros {filtroActivo == "SINIESTROS" ? `(${conteoSolicitudes.toString()})` : ""}</button>
                    {executiveResults.length > 0 ? (
                        <>
                            <span className='btn btn-sm' style={{ cursor: "default" }}>
                                Filtrar por ejecutivo:
                            </span>
                            <select className="form-select form-select-sm" style={{ borderColor: "#b1b1b1", borderRadius: "5px", width: "auto" }} value={executive} onChange={handleInputChange}>
                                <option value="">Todos</option>
                                {executiveResults.map(executiveItem => <option key={executiveItem.clave_usuario} value={executiveItem.usuario} selected={executive == executiveItem.usuario ? true : false}>{executiveItem.nombre_completo} - {executiveItem.usuario}</option>)}
                            </select>
                        </>
                    ) : (<></>)}
                </div>
                <div className="input-group input-group-sm shadow-sm" style={{ maxWidth: '300px' }}>
                    <span className="input-group-text"><i className="far fa-search"></i></span>
                    <input type="search" className="form-control border-start-0 ps-0" placeholder="Buscar folio, serie, cliente..." value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
                </div>
            </div>
            {cargando ? (<div className="p-5 text-center"><div className="spinner-border text-primary"></div></div>) : (<>
                <div className="table-responsive" style={{ minHeight: solicitudes.length === 0 ? '15vh' : '50vh' }}>
                    <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.85rem' }}>
                        <thead className="text-muted">
                            <tr>
                                <th onClick={() => handleSort('folio')} style={{ cursor: 'pointer', width: '12%' }}>Folio {sortConfig.key === 'folio' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
                                <th onClick={() => handleSort('clienteNombre')} style={{ cursor: 'pointer', width: '15%' }}>Cliente {sortConfig.key === 'clienteNombre' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
                                <th onClick={() => handleSort('numeroSerie')} style={{ cursor: 'pointer', width: '14%' }}>Bien {sortConfig.key === 'numeroSerie' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
                                <th style={{ width: '7%' }}>Cita</th>
                                <th style={{ width: '16%' }}>Comentarios</th>
                                <th onClick={() => handleSort('diasSla')} style={{ cursor: 'pointer', width: '8%' }} className="text-center">Alta solicitud {sortConfig.key === 'diasSla' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
                                <th onClick={() => handleSort('diasTaller')} style={{ cursor: 'pointer', width: '8%' }} className="text-center">En taller {sortConfig.key === 'diasTaller' && (sortConfig.direction === 'asc' ? '↑' : '↓')}</th>
                                {/* Se protege el ancho mínimo de Seguimiento para que no se apachurren los 7 círculos */}
                                <th style={{ cursor: 'pointer', width: '19%', minWidth: '190px' }} className="text-center">Seguimiento</th>
                                <th className="text-center" style={{ width: '6%' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {solicitudes.length === 0 ? (
                                <tr><td colSpan={8} className="text-center py-5 text-muted">No hay solicitudes en esta vista.</td></tr>
                            ) : (
                                solicitudes.map(solicitud => (
                                    <tr key={solicitud.id} style={{ borderLeft: `4px solid ${solicitud.colorBorde}`, }}>

                                        {/* COLUMNA 1: Folio e Info Básica */}
                                        <td>
                                            <a href={`/solicitudServicio?accion=view&id=${solicitud.id}`} className="fw-bold text-decoration-none">{solicitud.folio}</a>
                                            <span className="text-muted"> - {solicitud.ejecutivo}</span><br />
                                            <span className={solicitud.categoria.toLowerCase().includes('siniestro') ? 'text-danger fw-bold' : ''}>{solicitud.categoria}</span><br />
                                            <small className="text-muted">{solicitud.origen}</small>
                                        </td>

                                        {/* COLUMNA 2: Cliente y Contrato */}
                                        <td>
                                            <a href={`/cliente?id=${solicitud.clienteId}`} className="text-decoration-none">{solicitud.clienteNombre}</a><br />
                                            <a href={`/contrato?id=${solicitud.contratoId}`} className="text-decoration-none text-muted">{solicitud.contratoNombre}</a><br />
                                            {solicitud.flotilla && <small className="text-muted">{solicitud.flotilla}</small>}
                                        </td>

                                        {/* COLUMNA 3: Vehículo (Bien) */}
                                        <td>
                                            <a href={`/bien?id=${solicitud.bienId}`} className="text-decoration-none fw-bold">{solicitud.numeroSerie}</a><br />
                                            <span>{solicitud.marca} {solicitud.modelo} {solicitud.anio}</span><br />
                                            <small className="text-muted">{solicitud.version}</small>
                                        </td>

                                        {/* COLUMNA 4: Cita */}
                                        <td>
                                            {solicitud.fechaCita ? (
                                                <><span>{solicitud.fechaCita}</span><br /><small className="text-muted">{solicitud.taller}</small></>
                                            ) : (
                                                <span className="text-muted">---</span>
                                            )}
                                        </td>

                                        {/* COLUMNA 5: Comentarios */}
                                        <td>
                                            <span title={solicitud.comentarios}>
                                                {solicitud.comentarios.length > 80 ? solicitud.comentarios.substring(0, 80) + '...' : solicitud.comentarios}
                                            </span>
                                        </td>

                                        {/* COLUMNA 6: SLA */}
                                        <td className="text-center">
                                            {renderSlaBadge(solicitud.diasSla)}
                                        </td>

                                        {/* COLUMNA 6: SLA */}
                                        <td className="text-center">
                                            {solicitud.horasTaller > 0 ? renderTallerBadge(solicitud.horasTaller > 24 ? Math.ceil(solicitud.horasTaller / 24) : solicitud.horasTaller) : <span className="text-muted">---</span>}
                                        </td>

                                        {/* COLUMNA 7: Barra de Seguimiento */}
                                        <td className="text-center px-3">
                                            <ProgressBar pasoActual={solicitud.pasoSeguimiento} />
                                            <a href={`/solicitudServicio?accion=edit&id=${solicitud.id}`} className="btn btn-primary btn-sm w-100 mb-1 rounded-pill shadow-sm">
                                                {renderLabel(solicitud)}
                                            </a>
                                        </td>

                                        {/* COLUMNA 8: Acciones */}
                                        <td className="text-center">
                                            <div className="d-flex flex-column gap-1 align-items-center">
                                                <a href={`/solicitudServicio?accion=edit&id=${solicitud.id}`} className="btn btn-sm btn-warning" title="Editar">
                                                    <i className="far fa-list"></i>
                                                </a>
                                                <button className="btn btn-sm btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown">
                                                    <i className="fas fa-ellipsis-v"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="card-footer d-flex justify-content-between align-items-center">
                    <span className="text-muted small">
                        Mostrando {solicitudes.length > 0 ? ((paginaActual - 1) * registrosPorPagina) + 1 : 0} a {Math.min(paginaActual * registrosPorPagina, conteoTotalSolicitudes)} de {conteoTotalSolicitudes} registros
                    </span>

                    <nav>
                        <ul className="pagination pagination-sm mb-0 shadow-sm">
                            <li className={`page-item ${paginaActual === 1 ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => handleCambiarPagina(paginaActual - 1)}>&laquo; Anterior</button>
                            </li>

                            {/* Páginas Dinámicas (Simplificado para mostrar pág actual y totales) */}
                            <li className="page-item disabled">
                                <span className="page-link ">Página {paginaActual} de {totalPaginas}</span>
                            </li>

                            <li className={`page-item ${paginaActual === totalPaginas ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => handleCambiarPagina(paginaActual + 1)}>Siguiente &raquo;</button>
                            </li>
                        </ul>
                    </nav>
                </div>

            </>)}
        </div >
    );
};

export default DashboardSolicitudes;